import { useDebounce } from "../../../hooks/use-debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import ServiceAddonService, { ServiceAddon, ServiceAddonListFilters, ServiceAddonStatusCode } from "../services/service-addon-service";
import ServiceAddonTable, { ServiceAddonRow } from "../components/service-addon-table";
import ServiceAddonCreateModal, { ServiceAddonFormValues } from "../components/service-addon-create-modal";
import ServiceAddonDetailModal from "../components/service-addon-detail-modal";
import { ALL_BRANCHES_VALUE, useCatalogBranchOptions } from "../../../hooks/use-catalog-branch-options";


export const ServiceAddonIndexPage: React.FC = () => {
   // Stores từ API — có thêm lựa chọn "Dùng chung" (branch_id = 0)
  const { options: storeOptions, branchLabel } = useCatalogBranchOptions();
  // Tạm cố định "Dùng chung" (branch_id = 0) — danh mục đang vận hành dùng chung cho mọi chi nhánh.
  // Mở lại bộ lọc: đổi về useState<number | null>(null) + bỏ comment effect chọn chi nhánh mặc định và khối bộ lọc "Chi nhánh".
  const [selectedbranchId] = useState<number | null>(ALL_BRANCHES_VALUE);
//   useEffect(() => {
//   // So với null (không dùng !) — 0 là "Dùng chung", một lựa chọn hợp lệ
//   if (selectedbranchId === null && storeOptions.length > 0) {
//     setSelectedbranchId(Number(storeOptions[0].value));
//   }
// }, [storeOptions, selectedbranchId]);
  const [rows, setRows] = useState<ServiceAddonRow[]>([]);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<ServiceAddonRow | null>(null);
  const [detailRow, setDetailRow] = useState<ServiceAddonRow | null>(null);
  const [detailData, setDetailData] = useState<ServiceAddon | undefined>();

  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [keysearch, setKeysearch] = useState('');
  const [activeRecords, setActiveRecords] = useState(0);
  const [inactiveRecords, setInactiveRecords] = useState(0);
  const debouncedSearch = useDebounce(keysearch, 700);

 const { fetch: fetchDetail, loading: loadingDetail } = ServiceAddonService.detail();
  const { fetch: fetchServices, loading } = ServiceAddonService.list();
   const { fetch: createService, loading: creating } = ServiceAddonService.create();
   const { fetch: update, loading: updating } = ServiceAddonService.update();
   const { fetch: changeStatus, loading: changingStatus } = ServiceAddonService.changeStatus();
   const listRef = useRef(fetchServices);
   useEffect(() => {
    listRef.current = fetchServices;
  }, [fetchServices]);

  const load = useCallback(async () => {
    if (selectedbranchId == null) return;
    
    const status = tab === 'active' ? 1 : 0;
    const params: ServiceAddonListFilters = { 
      status,
      branch_id: selectedbranchId,
      limit,
      page,
      key_search: debouncedSearch,
    };
    const res = await listRef.current(params);
    if (!res) {
      showToast({ message: "Không thể tải danh sách addon", type: "error" });
      setRows([]);
      return;
    }
    if (isSuccess(res)) {
      setRows(
        res.data.list.map((d: ServiceAddon) => ({
          id: d.id,
          name: d.name,
          code: d.code,
          branchId: d.branch_id,
          storeName: branchLabel(d.branch_id),
          price: typeof d.price === 'number' ? d.price : (d.price ? Number(d.price) : undefined),
          statusCode: Number(d.status),
        }))
      );
      const total = res.data.total_record ?? 0;
      if (tab === 'active') {
        setActiveRecords(total);
      } else {
        setInactiveRecords(total);
      }
    } else {
      setRows([]);
    }
  }, [selectedbranchId, branchLabel, tab, limit, page, debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load, selectedbranchId, storeOptions, limit, page, debouncedSearch, tab]);

  const onCreate = async (v: ServiceAddonFormValues): Promise<boolean> => {
    const payload = {
      branch_id: Number(v.branchId),
      code: v.code.trim().toUpperCase(),
      name: v.name.trim(),
      description: v.description?.trim(),
      price: v.price ? Number(v.price.replace(/\D/g, '')) : undefined,
      status: v.isActive ? ServiceAddonStatusCode.ACTIVE : ServiceAddonStatusCode.INACTIVE,
    };
    const res = await createService(payload);
    if (!res) {
      showToast({ message: "Không thể tạo addon", type: "error" });
      return false;
    }
    if (isSuccess(res)) {
      showToast({ message: "Tạo addon thành công", type: "success" });
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể tạo addon", type: "error" });
    return false;
  };

  // Quyền tạo / đổi giá / bật-tắt do BE kiểm tra (product service: @Privileges trên ServicesAddonController) —
  // web không tự chặn nữa, thiếu quyền thì hiện thông báo lỗi BE trả về.
  const onEditOpen = (row: ServiceAddonRow) => {
    setEditRow(row);
  };

  const onEditSubmit = async (v: ServiceAddonFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const payload = {
      code: v.code.trim().toUpperCase(),
      name: v.name.trim(),
      description: v.description?.trim(),
      price: v.price ? Number(v.price.replace(/\D/g, '')) : undefined,
      status: v.isActive ? ServiceAddonStatusCode.ACTIVE : ServiceAddonStatusCode.INACTIVE,
    };
    const res = await update(editRow.id, payload);
    if (!res) {
      showToast({ message: "Không thể cập nhật addon", type: "error" });
      return false;
    }
    if (isSuccess(res)) {
      showToast({ message: "Cập nhật addon thành công", type: "success" });
      void load();
      setEditRow(null);
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật addon", type: "error" });
    return false;
  };

  const onRequestDeactivate = async (row: ServiceAddonRow) => {
    const res = await changeStatus(row.id, { status: ServiceAddonStatusCode.INACTIVE });
    if (!res) {
      showToast({ message: "Không thể cập nhật trạng thái", type: "error" });
      return;
    }
    if (isSuccess(res)) {
      showToast({ message: "Đã tạm ngưng addon", type: "success" });
      void load();
    } else {
      showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
    }
  };

  const onRequestActivate = async (row: ServiceAddonRow) => {
    const res = await changeStatus(row.id, { status: ServiceAddonStatusCode.ACTIVE });
    if (!res) {
      showToast({ message: "Không thể cập nhật trạng thái", type: "error" });
      return;
    }
    if (isSuccess(res)) {
      showToast({ message: "Đã kích hoạt addon", type: "success" });
      void load();
    } else {
      showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
    }
  };
  const handleViewDetail = async (row: ServiceAddonRow) => {
      setDetailRow(row);
      setDetailData(undefined);
  
    try {
      const response = await fetchDetail(row.id);
      if (!response) {
        showToast({
          message: "Không thể tải chi tiết dịch vụ",
          type: "error",
        });
        setDetailRow(null);
        return;
      }

      if (isSuccess(response)) {
        setDetailData(response.data);
      } else {
        showToast({
          message: response.message ?? "Không thể tải chi tiết dịch vụ",
          type: "error",
        });
        setDetailRow(null);
      }
    } catch (error) {
      showToast({
        message:
          error instanceof Error
            ? error.message
            : "Không thể tải chi tiết dịch vụ",
        type: "error",
      });
      setDetailRow(null);
    }
  };
  const handleCloseDetailModal = () => {
    setDetailRow(null);
    setDetailData(undefined);
  };
  return (
    <div className="">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">Danh mục dịch vụ đi kèm</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => setOpen(true)}  className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
        >
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM DỊCH VỤ ĐI KÈM</span>
        </button>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
        <div className="flex flex-col gap-3 rounded-2xl bg-slate-100 px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-8">
            <button onClick={() => setTab("active")} className={`relative flex items-center gap-2 pb-2 text-sm font-semibold uppercase tracking-wide transition ${tab === "active" ? "text-green-600" : "text-slate-400 hover:text-slate-600"}`}>
              <span>Đang hoạt động</span>
              <span className={`${tab === "active" ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-500"} rounded-full px-2.5 py-0.5 text-xs font-semibold`}>{activeRecords}</span>
              {tab === "active" ? <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-1 rounded-full bg-green-500"></span> : null}
            </button>
            <button onClick={() => setTab("inactive")} className={`relative flex items-center gap-2 pb-2 text-sm font-semibold uppercase tracking-wide transition ${tab === "inactive" ? "text-green-600" : "text-slate-400 hover:text-slate-600"}`}>
              <span>Tạm ngưng</span>
              <span className={`${tab === "inactive" ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-500"} rounded-full px-2.5 py-0.5 text-xs font-semibold`}>{inactiveRecords}</span>
              {tab === "inactive" ? <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-1 rounded-full bg-green-500"></span> : null}
            </button>
          </div>
          {/* Bộ lọc chi nhánh — tạm ẩn, đang cố định "Dùng chung" (branch_id = 0)
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-600">Chi nhánh:</label>
            <select
              value={selectedbranchId ?? ""}
              onChange={(e) => setSelectedbranchId(Number(e.target.value))}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {loadingStores && <option>Đang tải...</option>}
              {!loadingStores && storeOptions.length === 0 && <option>Không có chi nhánh</option>}
              {storeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div> */}
        </div>

        <div className="mt-6">
          <ServiceAddonTable 
            rows={rows} 
            isLoading={loading} 
            startIndex={(page - 1) * limit + 1} 
            onEdit={onEditOpen} 
            onDeactivate={tab === 'active' ? onRequestDeactivate : undefined}
            onActivate={tab === 'inactive' ? onRequestActivate : undefined}
            onViewDetail={handleViewDetail}
            page={page}
            pageSize={limit}
            search={keysearch}
            setPage={setPage}
            setPageSize={setLimit}
            setSearch={setKeysearch}
            totalRecords={tab === 'active' ? activeRecords : inactiveRecords}
          />
        </div>
      </div>

      {open ? (
        <ServiceAddonCreateModal  storeOptions={[
          ...storeOptions.filter((o) => Number(o.value) === selectedbranchId),
          ...storeOptions.filter((o) => Number(o.value) !== selectedbranchId),
        ]} onClose={() => setOpen(false)} onSubmit={onCreate} isSubmitting={creating} initialValues={{ branchId: selectedbranchId  ?? 0 }} />
      ) : null}

      {editRow ? (
        <ServiceAddonCreateModal
          storeOptions={[
            ...storeOptions.filter((o) => Number(o.value) === selectedbranchId),
            ...storeOptions.filter((o) => Number(o.value) !== selectedbranchId),
          ]}
          onClose={() => setEditRow(null)}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          titleText="Cập nhật Addon"
          submitText="Cập nhật"
          initialValues={{
            code: editRow.code,
            name: editRow.name,
            branchId: editRow.branchId ?? Number(storeOptions[0]?.value ?? 0),
            price: editRow.price ? String(editRow.price) : "",
            isActive: (editRow.statusCode ?? 1) === 1,
          }}
        />
      ) : null}
      {detailRow ? (
              <ServiceAddonDetailModal
                rowName={detailRow.name}
                data={detailData}
                loading={loadingDetail}
                onClose={handleCloseDetailModal}
              />
            ) : null}
    </div>
  );
};

export default ServiceAddonIndexPage;
